/**
 * `<SymbolSearch>` — the Symbol control on a chart's toolbar.
 *
 * ## The box is a label; the dialog is the editor
 *
 * At rest it shows `AAPL - Apple Inc.`, not `AAPL`. That reads as an editable
 * text field and is not one: the moment a user typed into it they would be
 * editing a string that is half ticker and half company name, and there is no
 * sane thing to do with the result. So **the box is `readOnly`, and the first
 * printable keystroke asks the app to open the search** carrying that character
 * across as the initial query.
 *
 * The hand-off is a `preventDefault()` on `keydown`, not an `onChange` that
 * reads the box afterwards. That ordering is the whole trick. `onChange` fires
 * *after* the character has been inserted, so the seed would have to be
 * recovered from `AAPL - Apple Inc.m` — the label with a stray letter in it, at
 * whatever offset the caret happened to sit. Intercepting on `keydown` means
 * the box is never mutated at all. `readOnly` then closes the remaining doors —
 * paste and IME composition cannot reach a read-only input, so there is no
 * fourth path that has to be caught.
 *
 * What *leaves* here is still a bare ticker. `SYMBOL - Name` is presentation:
 * `onCommit` feeds `ChartGroup.symbol` and from there the data request, and
 * `"AAPL - Apple Inc."` is not a symbol any server has heard of. `commit` cuts
 * at the first whitespace for that reason — a ticker has no spaces in it, so
 * that rule cannot damage a real one.
 *
 * ## The dialog is not ours
 *
 * This box used to render its own `<SymbolDialog>`, and it is mounted once per
 * chart toolbar — so a two-chart layout had two dialogs, app-wide typing needed
 * a subscriber set to decide which one answered, and the answer could land on
 * an instance whose state was cold. There is now one dialog, owned by the app,
 * and this asks for it through `onOpenSearch`. The caller knows which chart the
 * result belongs to; nothing here has to.
 *
 * ## No index is a working box, not a broken one
 *
 * With no symbol service configured this renders a plain free-text input that
 * commits exactly as it did before any of this existed — not read-only, no
 * dialog, Enter and blur commit what was typed. The dialog is the enhancement;
 * the input is the feature. Anything that inverts those two makes the app
 * unusable without a symbol service, which is the state most developers run it
 * in. `symbolsConfigured` decides that statically, so it never depends on
 * whether a fetch has landed.
 *
 * Commit happens on Enter, on selection, and on blur — never per keystroke.
 * That is not typeahead bookkeeping: `symbol` is part of `ChartGroupView`'s
 * `structuralKey`, so a per-keystroke commit tore down the WebGL surface and
 * refetched once per character, and typing `AAPL` cost four rebuilds to reach
 * one chart.
 *
 * Colours are `var(--qf-*)` only — this is chrome, and chrome inherits.
 */

import { useRef, useState } from "react";

import { symbolsConfigured } from "../api/symbols";
import { formatComposite, isComposite } from "../lib/composite";
import { symbolLabel, useSymbolName } from "../lib/symbol-index";
import { isIndexSymbol, symbolTitle } from "../lib/topdown";

export interface SymbolSearchProps {
  /** The committed symbol — workspace state, and the box's resting value. */
  value: string;
  /** Called with the trimmed, uppercased ticker, and only when it changes. */
  onCommit: (symbol: string) => void;
  /**
   * Ask the app to open the search, seeded with `query`.
   *
   * `select` means "arrive with the seed selected", which is what a click
   * wants: it opens on the symbol already charted, so the user is one keystroke
   * from replacing it. A typed letter arrives bare, with the caret after it.
   *
   * Optional so the box still works in isolation — with no handler it is a
   * plain input, which is the same shape as the unconfigured path.
   */
  onOpenSearch?: (query: string, select: boolean) => void;
  /** The host toolbar's control styling, so the box matches its siblings. */
  style?: React.CSSProperties;
  /** Ties the box to the toolbar's visible "Symbol" label. */
  id?: string;
  /** The narrowest toolbar's form: `AAPL`, not `AAPL - Apple Inc.` */
  tickerOnly?: boolean;
}

export function SymbolSearch({
  value,
  onCommit,
  onOpenSearch,
  style,
  id,
  tickerOnly = false,
}: SymbolSearchProps) {
  const [committed, setCommitted] = useState(value);
  /**
   * `null` means the box is showing the committed label rather than a draft.
   *
   * A draft only ever exists on the degraded path — no service, so no dialog to
   * hand the keystrokes to, so the box is a plain editable input again. Keeping
   * the two states in one nullable field is what lets `readOnly` and the blur
   * commit be one-line rules instead of a mode flag they could disagree about.
   */
  const [draft, setDraft] = useState<string | null>(null);
  // Hover and focus in state rather than as CSS: every style in this file is an
  // inline object, which is what keeps the component self-contained and all its
  // colours `var(--qf-*)`. There is no stylesheet here to hold a `:focus` rule.
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // The symbol also changes from outside — picking a row in the screener
  // dispatches `setSymbol`. Adopting it during render (React's own pattern for
  // derived state) keeps the box showing the truth rather than a stale draft.
  if (value !== committed) {
    setCommitted(value);
    setDraft(null);
  }

  // The hook runs unconditionally — a composite must not skip it, or the call
  // order changes with the symbol. A composite has no company name to pair
  // with, so `symbolLabel` would show the raw string; `AAPL / SPY` is the same
  // value spaced for reading. Display only: `commit` cuts at the first space,
  // so this must never be fed back in.
  const name = useSymbolName(committed);
  const label = isIndexSymbol(committed)
    ? symbolTitle(committed)
    : isComposite(committed)
    ? formatComposite(committed)
    : tickerOnly
      ? committed
      : symbolLabel(committed, name);
  /**
   * Read-only exactly when the box is a label.
   *
   * On `symbolsConfigured` rather than on whether the index has loaded: the two
   * used to be conflated, so the box was briefly editable on a cold page and
   * the first click merely focused it. Whether a *dialog exists behind this* is
   * a static fact about the deployment.
   */
  const searchable = symbolsConfigured && onOpenSearch !== undefined;
  const readOnly = searchable && draft === null;

  const commit = (raw: string) => {
    setDraft(null);
    // A ticker has no whitespace in it, so cutting at the first space cannot
    // damage a real one — and it is the last line of defence against a label
    // ("AAPL - Apple Inc.") reaching `ChartGroup.symbol` and the data request.
    const next = raw.trim().split(/\s/)[0]!.toUpperCase();
    // An empty box restores rather than committing "" — a chart group with no
    // symbol is a rebuilt surface with nothing to draw, which is never what
    // clearing the field meant.
    if (!next) return;
    if (next !== committed) {
      setCommitted(next);
      onCommit(next);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      // Enter means "commit what I typed" whenever there is something typed.
      if (draft !== null) commit(draft);
      else if (searchable) onOpenSearch!("", false);
      return;
    }
    if (event.key === "Escape") {
      if (draft !== null) {
        event.preventDefault();
        setDraft(null);
      }
      return;
    }

    // No dialog behind us: every other key belongs to a plain text input,
    // including Tab.
    if (!searchable) return;

    // Space would seed the query with a character no ticker contains, so it
    // opens the browse list instead — same as clicking the box.
    if (event.key === "ArrowDown" || event.key === " ") {
      event.preventDefault();
      onOpenSearch!(draft ?? "", false);
      return;
    }
    // One printable character, unmodified: the hand-off. `preventDefault` is
    // what keeps it out of the box *and* what stops the app-wide listener
    // seeding a second time — see `use-type-to-search.ts`.
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      onOpenSearch!((draft ?? "") + event.key, false);
    }
  };

  return (
    <input
      id={id}
      ref={inputRef}
      value={draft ?? label}
      readOnly={readOnly}
      role="combobox"
      aria-haspopup="dialog"
      autoComplete="off"
      spellCheck={false}
      // The label is usually wider than the box; hovering has to be able to
      // recover the rest of it.
      title={draft === null ? label : undefined}
      onFocus={() => setFocused(true)}
      onBlurCapture={() => setFocused(false)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => {
        // Clicking a label-mode box means "search", not "put a caret here".
        //
        // Seeded with the symbol already charted, and **selected**: opening on
        // the thing you are looking at is the useful default — it is right
        // there to read, to edit a character of, or to replace by typing.
        // Opening empty would throw away the one piece of context the click
        // already carried.
        //
        // The typed path deliberately does not do this: a letter typed at the
        // page means "find something else", so it seeds with that letter alone.
        if (searchable && draft === null) onOpenSearch!(value, true);
      }}
      onChange={(event) => {
        // Only reachable with no dialog — a read-only input has no change.
        setDraft(event.target.value);
      }}
      onKeyDown={onKeyDown}
      onBlur={() => {
        if (draft !== null) commit(draft);
      }}
      // A label opens a dialog and a draft takes a caret; the pointer has to
      // say which of the two this box currently is.
      style={{
        ...controlStyle,
        ...(hovered && !focused ? controlHoverStyle : {}),
        ...(focused ? controlFocusStyle : {}),
        cursor: readOnly ? "pointer" : "text",
        ...style,
      }}
    />
  );
}

/**
 * The toolbar box at rest — deliberately barely a box.
 *
 * It reads as the chart's title rather than as a form field, which is what it
 * is: the symbol you are looking at, sitting above the chart. A filled well
 * with a visible border says "type here", and typing here is exactly what this
 * control does not want — it opens a dialog instead.
 *
 * So the background is `transparent` (it takes the toolbar's own colour, in any
 * theme, with no token needing to match) and the border is `--qf-border`, which
 * is the divider colour: present enough to keep the click target findable,
 * quiet enough not to read as an input.
 */
const controlStyle: React.CSSProperties = {
  background: "transparent",
  color: "var(--qf-text)",
  border: "1px solid transparent",
  borderRadius: 4,
  padding: "0.25rem 0.4rem",
  fontSize: "0.85rem",
  fontWeight: 600,
  // The box holds a label now, not a ticker, so it will overflow; clipping with
  // an ellipsis beats a value that scrolls under the caret.
  textOverflow: "ellipsis",
  // Only the border and background move on focus/hover. Not the padding, and
  // not the border *width* — either would reflow the toolbar under the pointer.
  transition: "background 120ms, border-color 120ms",
};

/**
 * Focused or hovered: the box becomes a control.
 *
 * The border takes `--qf-accent` on focus — the same signal every other focused
 * thing in the app uses — and hover only lifts the background, so pointing at
 * it is a weaker hint than being on it. Both change colour alone, never
 * geometry.
 */
const controlFocusStyle: React.CSSProperties = {
  background: "var(--qf-surface-alt)",
  borderColor: "var(--qf-accent)",
};

const controlHoverStyle: React.CSSProperties = {
  background: "var(--qf-surface-alt)",
  borderColor: "var(--qf-border)",
};
